import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { confirmDelete } from '../../utils/confirm';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function CourseModal({ tenantId, course, onClose, onSaved }) {
  const isEdit = !!course;
  const [form, setForm] = useState({
    courseCode: course?.CourseCode || '',
    courseName: course?.CourseName || '',
    credits: course?.Credits != null ? course.Credits : '',
    isActive: course ? course.IsActive : true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.courseCode.trim()) return toast.error('Course Code is required');
    if (!form.courseName.trim()) return toast.error('Course Name is required');
    if (form.credits === '' || isNaN(parseFloat(form.credits))) return toast.error('Credits must be a valid number');

    setSaving(true);
    try {
      const payload = {
        tenantId,
        courseCode: form.courseCode.trim(),
        courseName: form.courseName.trim(),
        credits: parseFloat(form.credits),
        isActive: form.isActive,
      };
      if (isEdit) {
        await api.put(`/courses/${course.CourseID}`, payload);
        toast.success('Course updated');
      } else {
        await api.post('/courses', payload);
        toast.success('Course created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Edit Course' : 'Add Course'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="required-label">Course Code</label>
            <input type="text" placeholder="e.g., CS-101" value={form.courseCode} onChange={(e) => setForm({ ...form, courseCode: e.target.value })} autoFocus />
          </div>
          <div className="form-group">
            <label className="required-label">Course Name</label>
            <input type="text" placeholder="e.g., Introduction to Computer Science" value={form.courseName} onChange={(e) => setForm({ ...form, courseName: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="required-label">Credits</label>
            <input type="number" step="0.1" placeholder="e.g., 4" value={form.credits} onChange={(e) => setForm({ ...form, credits: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="checkbox-inline">
              <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
              Is Active
            </label>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-cancel-text" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-save" disabled={saving}>{saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ImportModal({ tenantId, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const handleDownloadTemplate = () => {
    const token = localStorage.getItem('qposm_token');
    const link = document.createElement('a');
    link.href = `${API_BASE}/courses/template/courses`;
    link.style.display = 'none';
    // Use fetch with auth header for template download
    fetch(`${API_BASE}/courses/template/courses`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.download = 'Course_Import_Template.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
  };

  const handleImport = async () => {
    if (!file) return toast.error('Please select a file');
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tenantId', tenantId);
      const res = await api.post('/courses/import/courses', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      if (res.data.imported > 0) toast.success(`${res.data.imported} course(s) imported`);
      if (res.data.discrepancies?.length > 0) toast.warn(`${res.data.discrepancies.length} discrepancy(ies) found`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadDiscrepancies = async () => {
    try {
      const res = await api.post('/courses/discrepancies/courses', { discrepancies: result.discrepancies }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Course_Import_Discrepancies.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download discrepancies');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
        <h2>Import Courses</h2>

        {!result ? (
          <>
            <div className="import-steps">
              <div className="import-step">
                <span className="import-step-num">1</span>
                <div>
                  <strong>Download Template</strong>
                  <p style={{ fontSize: 13, color: '#616161', margin: '4px 0' }}>Download the Excel template with required columns and sample data.</p>
                  <button className="btn btn-outline btn-sm" onClick={handleDownloadTemplate}>Download Template</button>
                </div>
              </div>
              <div className="import-step">
                <span className="import-step-num">2</span>
                <div>
                  <strong>Upload File</strong>
                  <p style={{ fontSize: 13, color: '#616161', margin: '4px 0' }}>Fill in the template and upload. Columns: Course Code, Course Name, Credits, Is Active.</p>
                  <div className="file-upload-area">
                    <input type="file" accept=".xlsx,.xls" ref={fileRef} className="file-input" id="import-file" onChange={(e) => setFile(e.target.files[0])} />
                    <label htmlFor="import-file" className="file-upload-label">
                      <span className="file-upload-icon">📎</span>
                      {file ? file.name : 'Choose Excel file (.xlsx)'}
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="import-note">
              <strong>Note:</strong> Duplicate check is based on Course Code + Course Name + Credits. Records matching all three will be skipped.
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-cancel-text" onClick={onClose}>Cancel</button>
              <button className="btn btn-save" onClick={handleImport} disabled={!file || importing}>
                {importing ? 'Importing...' : 'Import'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="import-result-summary">
              <div className="import-stat import-stat-success">
                <span className="import-stat-num">{result.imported}</span>
                <span>Imported</span>
              </div>
              <div className={`import-stat ${result.discrepancies?.length > 0 ? 'import-stat-error' : 'import-stat-success'}`}>
                <span className="import-stat-num">{result.discrepancies?.length || 0}</span>
                <span>Discrepancies</span>
              </div>
            </div>

            {result.discrepancies?.length > 0 && (
              <>
                <div style={{ maxHeight: 250, overflowY: 'auto', marginBottom: 16 }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Course Code</th>
                        <th>Course Name</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.discrepancies.map((d, i) => (
                        <tr key={i}>
                          <td>{d.row}</td>
                          <td>{d.courseCode}</td>
                          <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.courseName}</td>
                          <td style={{ color: '#c62828', fontSize: 13 }}>{d.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button className="btn btn-outline btn-sm" onClick={handleDownloadDiscrepancies} style={{ marginBottom: 16 }}>
                  Download Discrepancies
                </button>
              </>
            )}

            <div className="form-actions">
              <button className="btn btn-save" onClick={() => { onDone(); onClose(); }}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CoursesTab({ tenantId }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const pageSize = 50;
  const searchTimer = useRef();

  const loadCourses = useCallback(async (p = page, s = search) => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const res = await api.get(`/courses/list/${tenantId}`, { params: { page: p, pageSize, search: s } });
      if (res.data.success) {
        setCourses(res.data.data);
        setPagination(res.data.pagination);
      }
    } catch {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  }, [tenantId, page, search]);

  useEffect(() => { loadCourses(); }, [loadCourses]);

  const handleSearchChange = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 400);
  };

  const handleDelete = async (courseId) => {
    if (!(await confirmDelete('Course'))) return;
    try {
      await api.delete(`/courses/${courseId}`);
      toast.success('Course deleted');
      loadCourses();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('qposm_token');
      const res = await fetch(`${API_BASE}/courses/export/courses/${tenantId}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Courses_Export.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  return (
    <>
      <div className="page-card-header" style={{ margin: 0, paddingBottom: 16, borderBottom: '1px solid #e0e0e0' }}>
        <h3>Courses {pagination.total > 0 && <span style={{ fontWeight: 400, fontSize: 14, color: '#616161' }}>({pagination.total})</span>}</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={handleExport} title="Export to Excel">Export</button>
          <button className="btn btn-outline btn-sm" onClick={() => setShowImport(true)}>Import</button>
          <button className="btn btn-add" onClick={() => { setEditCourse(null); setShowModal(true); }}>+ Add Course</button>
        </div>
      </div>

      <div className="user-filters" style={{ marginTop: 16 }}>
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input type="text" placeholder="Search by code or name..." value={searchInput} onChange={(e) => handleSearchChange(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
      ) : courses.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          {search ? 'No courses match your search.' : 'No courses found. Add courses manually or import from Excel.'}
        </div>
      ) : (
        <>
          <table className="data-table" style={{ marginTop: 16 }}>
            <thead>
              <tr>
                <th>Course Code</th>
                <th>Course Name</th>
                <th>Credits</th>
                <th>Topics</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {courses.map((c) => (
                <tr key={c.CourseID}>
                  <td><strong>{c.CourseCode}</strong></td>
                  <td>{c.CourseName}</td>
                  <td style={{ textAlign: 'center' }}>{c.Credits}</td>
                  <td><span className="user-count-badge">{c.TopicCount}</span></td>
                  <td>
                    <span className={`badge ${c.IsActive ? 'badge-active' : 'badge-inactive'}`}>
                      {c.IsActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="action-btn action-edit" title="Edit" onClick={() => { setEditCourse(c); setShowModal(true); }}>✏</button>
                      <button className="action-btn action-delete" title="Delete" onClick={() => handleDelete(c.CourseID)}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination-bar">
              <button className="btn btn-outline btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
              <span className="pagination-info">Page {page} of {pagination.totalPages}</span>
              <button className="btn btn-outline btn-sm" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          )}
        </>
      )}

      {showModal && (
        <CourseModal
          tenantId={tenantId}
          course={editCourse}
          onClose={() => { setShowModal(false); setEditCourse(null); }}
          onSaved={() => { setShowModal(false); setEditCourse(null); loadCourses(); }}
        />
      )}
      {showImport && (
        <ImportModal
          tenantId={tenantId}
          onClose={() => setShowImport(false)}
          onDone={() => { setPage(1); loadCourses(1, search); }}
        />
      )}
    </>
  );
}

export default CoursesTab;
